import { TimeRangeValidationResult } from '../models/cars-widget.interface';
import {
  DROPOFF_BEFORE_PICKUP,
  INSUFFICIENT_TIME_RANGE
} from './error-constants';

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function isDropoffTimeValid(pickupTime: string, dropoffTime: string): boolean {
  return timeToMinutes(dropoffTime) > timeToMinutes(pickupTime);
}

function isTimeRangeValid(
  pickupDate: Date,
  dropoffDate: Date,
  pickupTime: string,
  dropoffTime: string
): TimeRangeValidationResult {
  const pickupDateTime = new Date(pickupDate);
  const dropoffDateTime = new Date(dropoffDate);
  const [pickupHour, pickupMin] = pickupTime.split(':').map(Number);
  const [dropoffHour, dropoffMin] = dropoffTime.split(':').map(Number);
  pickupDateTime.setHours(pickupHour, pickupMin, 0, 0);
  dropoffDateTime.setHours(dropoffHour, dropoffMin, 0, 0);
  const diffMs = dropoffDateTime.getTime() - pickupDateTime.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 0)
    return { isValid: false, errorType: DROPOFF_BEFORE_PICKUP };
  if (diffHours < 2)
    return { isValid: false, errorType: INSUFFICIENT_TIME_RANGE };
  return { isValid: true };
}

function areDatesValid(pickup: number, dropoff: number): boolean {
  return dropoff >= pickup;
}

function isDateRangeValid(pickup: number, dropoff: number): boolean {
  const daysDiff = (dropoff - pickup) / (1000 * 60 * 60 * 24);
  return daysDiff <= 30;
}

function validateSameDayTimes(
  pickup: number,
  dropoff: number,
  pickupTime: string | null,
  dropoffTime: string | null
): boolean {
  if (dropoff !== pickup) return true;
  if (!pickupTime || !dropoffTime) return true;
  return isDropoffTimeValid(pickupTime, dropoffTime);
}

export function validateDropoffDateTime(
  pickupDate: Date | null,
  dropoffDate: Date | null,
  pickupTime: string | null,
  dropoffTime: string | null
): boolean {
  if (!pickupDate || !dropoffDate) return true;

  const pickup = new Date(pickupDate).setHours(0, 0, 0, 0);
  const dropoff = new Date(dropoffDate).setHours(0, 0, 0, 0);

  if (!areDatesValid(pickup, dropoff)) return false;
  return validateSameDayTimes(pickup, dropoff, pickupTime, dropoffTime);
}

export function validateTimeRange(
  pickupDate: Date | null,
  dropoffDate: Date | null,
  pickupTime: string | null,
  dropoffTime: string | null
): TimeRangeValidationResult {
  if (hasInvalidInputs(pickupDate, dropoffDate, pickupTime, dropoffTime)) {
    return { isValid: true };
  }

  const { pickup, dropoff } = getNormalizedDates(
    pickupDate as Date,
    dropoffDate as Date
  );
  if (!areDatesValid(pickup, dropoff)) return { isValid: true };
  return isTimeRangeValid(
    pickupDate as Date,
    dropoffDate as Date,
    pickupTime as string,
    dropoffTime as string
  );
}

function hasInvalidInputs(
  pickupDate: Date | null,
  dropoffDate: Date | null,
  pickupTime: string | null,
  dropoffTime: string | null
): boolean {
  return !pickupDate || !dropoffDate || !pickupTime || !dropoffTime;
}

function getNormalizedDates(
  pickupDate: Date,
  dropoffDate: Date
): { pickup: number; dropoff: number } {
  return {
    pickup: new Date(pickupDate).setHours(0, 0, 0, 0),
    dropoff: new Date(dropoffDate).setHours(0, 0, 0, 0)
  };
}

export function validateDateRange(
  pickupDate: Date | null,
  dropoffDate: Date | null
): boolean {
  if (!pickupDate || !dropoffDate) return true;

  const pickup = new Date(pickupDate).setHours(0, 0, 0, 0);
  const dropoff = new Date(dropoffDate).setHours(0, 0, 0, 0);

  if (!areDatesValid(pickup, dropoff)) return true;
  return isDateRangeValid(pickup, dropoff);
}

export function validatePickupDropoffTimeNotPassed(
  pickupDate: Date | null,
  pickupTime: string | null
): boolean {
  if (!pickupDate || !pickupTime) return true;
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const pickupDateOnly = new Date(
    pickupDate.getFullYear(),
    pickupDate.getMonth(),
    pickupDate.getDate()
  );
  if (todayStart.getTime() !== pickupDateOnly.getTime()) return true;

  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const pickupMinutes = timeToMinutes(pickupTime);
  return pickupMinutes > currentMinutes;
}
